import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import type { AccountBindingReviewController } from '../../services/accountBindingReviewService';

interface Props {
  open: boolean;
  controller: AccountBindingReviewController;
  kind?: 'binding' | 'unclaim';
  onClose: () => void;
  onApplied?: () => void;
}

interface ProfileSummary {
  userId: string;
  personId: number | null;
  authUid?: string | null;
  displayName?: string;
}
interface ReviewSummary {
  kind: 'binding';
  authUid: string;
  targetPersonId: number;
  formerUserId: string | null;
  targetUserId: string;
  former: ProfileSummary | null;
  target: ProfileSummary;
  direct: ProfileSummary | null;
}
interface UnclaimSummary {
  kind: 'unclaim';
  authUid: string;
  formerUserId: string;
  newUserId: string;
  newPersonId: number;
  keepPersonalDetails: boolean;
  former: ProfileSummary;
  newPerson: ProfileSummary;
  direct: ProfileSummary | null;
}
type AnyReviewSummary = ReviewSummary | UnclaimSummary;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const nonempty = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;
const profileSummary = (value: unknown): ProfileSummary | null => {
  if (!isRecord(value) || !nonempty(value.userId)
    || (value.personId !== null && (!Number.isSafeInteger(value.personId) || Number(value.personId) <= 0))
    || (value.authUid !== undefined && value.authUid !== null && typeof value.authUid !== 'string')
    || (value.displayName !== undefined && typeof value.displayName !== 'string')) return null;
  return {
    userId: value.userId,
    personId: value.personId === null ? null : Number(value.personId),
    ...(value.authUid === null || typeof value.authUid === 'string' ? { authUid: value.authUid } : {}),
    ...(typeof value.displayName === 'string' ? { displayName: value.displayName } : {}),
  };
};
const reviewSummary = (input: unknown, profiles: unknown, kind: 'binding' | 'unclaim'): AnyReviewSummary | null => {
  if (kind === 'unclaim') {
    if (!isRecord(input) || !nonempty(input.authUid) || !nonempty(input.formerUserId)
      || !nonempty(input.newUserId) || !Number.isSafeInteger(input.newPersonId)
      || Number(input.newPersonId) <= 0 || typeof input.keepPersonalDetails !== 'boolean'
      || !Object.prototype.hasOwnProperty.call(input, 'directUpdateTime')
      || !isRecord(profiles)) return null;
    const former = profileSummary(profiles.former);
    const newPerson = profileSummary(profiles.newPerson);
    const direct = profiles.direct == null ? null : profileSummary(profiles.direct);
    if (!former || former.userId !== input.formerUserId || !former.personId
      || former.personId === Number(input.newPersonId)
      || !newPerson || newPerson.userId !== input.newUserId || newPerson.personId !== Number(input.newPersonId)
      || newPerson.authUid !== input.authUid
      || (input.directUpdateTime !== null
        ? !direct || direct.userId !== input.authUid || direct.userId === input.formerUserId || direct.userId === input.newUserId
        : profiles.direct != null)) return null;
    return { kind, authUid: input.authUid, formerUserId: input.formerUserId,
      newUserId: input.newUserId, newPersonId: Number(input.newPersonId),
      keepPersonalDetails: input.keepPersonalDetails, former, newPerson, direct };
  }
  if (!isRecord(input) || !nonempty(input.authUid) || !nonempty(input.targetUserId)
    || (input.formerUserId !== null && !nonempty(input.formerUserId))
    || !Number.isSafeInteger(input.targetPersonId) || Number(input.targetPersonId) <= 0 || !isRecord(profiles)) return null;
  const target = profileSummary(profiles.target);
  const former = profiles.former === null ? null : profileSummary(profiles.former);
  const hasDirectVersion = Object.prototype.hasOwnProperty.call(input, 'directUpdateTime');
  const directVersion = input.directUpdateTime;
  if (hasDirectVersion && (!isRecord(directVersion) || !Number.isSafeInteger(directVersion.seconds)
    || !Number.isInteger(directVersion.nanoseconds) || Number(directVersion.nanoseconds) < 0
    || Number(directVersion.nanoseconds) >= 1_000_000_000)) return null;
  const direct = profiles.direct == null ? null : profileSummary(profiles.direct);
  if (!target || target.personId !== Number(input.targetPersonId) || target.userId !== input.targetUserId
    || (input.formerUserId === null ? profiles.former !== null
      : !former || former.userId !== input.formerUserId)
    || (hasDirectVersion
      ? !direct || direct.userId !== input.authUid || direct.userId === input.formerUserId || direct.userId === input.targetUserId
      : profiles.direct != null)) return null;
  return { kind, authUid: input.authUid, targetPersonId: Number(input.targetPersonId),
    formerUserId: input.formerUserId, targetUserId: input.targetUserId, former, target,
    direct: hasDirectVersion ? direct : null };
};

const AccountBindingReviewDialog: React.FC<Props> = ({ open, controller, kind = 'binding', onClose, onApplied }) => {
  const [busy, setBusy] = useState<'loading' | 'approving' | 'applying' | null>(null);
  const [summary, setSummary] = useState<AnyReviewSummary | null>(null);
  const [reviewAcknowledged, setReviewAcknowledged] = useState(false);
  const [approved, setApproved] = useState(false);
  const [applied, setApplied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const generation = useRef(0);

  useEffect(() => {
    generation.current += 1;
    setBusy(null);
    setSummary(null);
    setReviewAcknowledged(false);
    setApproved(false);
    setApplied(false);
    setError(null);
    return () => { generation.current += 1; };
  }, [open, controller, kind]);

  const close = () => {
    if (busy) return;
    generation.current += 1;
    setSummary(null);
    setReviewAcknowledged(false);
    setApproved(false);
    setApplied(false);
    setError(null);
    onClose();
  };

  const loadCurrentPlan = async () => {
    const requestGeneration = generation.current;
    const isCurrent = () => generation.current === requestGeneration;
    setBusy('loading');
    setError(null);
    setSummary(null);
    setReviewAcknowledged(false);
    setApproved(false);
    setApplied(false);
    try {
      await controller.prepare();
      if (!isCurrent()) return;
      const preview = await controller.preview();
      if (!isCurrent()) return;
      const safeSummary = reviewSummary(preview.input, preview.profiles, kind);
      if (!safeSummary) throw new Error('Invalid preview shape.');
      setSummary(safeSummary);
    } catch {
      if (isCurrent()) setError('Could not load a current account binding plan. Retry to continue.');
    } finally {
      if (isCurrent()) setBusy(null);
    }
  };

  const approvePlan = async () => {
    const requestGeneration = generation.current;
    const isCurrent = () => generation.current === requestGeneration;
    setBusy('approving');
    setError(null);
    try {
      await controller.approve();
      if (isCurrent()) setApproved(true);
    } catch {
      if (isCurrent()) setError('The plan was not approved. Retry this step to continue.');
    } finally {
      if (isCurrent()) setBusy(null);
    }
  };

  const applyBinding = async () => {
    const requestGeneration = generation.current;
    const isCurrent = () => generation.current === requestGeneration;
    setBusy('applying');
    setError(null);
    try {
      await controller.apply();
    } catch {
      if (isCurrent()) {
        setError('The account binding was not confirmed. Retry apply to continue.');
        setBusy(null);
      }
      return;
    }
    if (!isCurrent()) return;
    setApplied(true);
    setBusy(null);
    onApplied?.();
  };

  const disabled = busy !== null;
  return (
    <Dialog
      open={open}
      onClose={close}
      disableEscapeKeyDown={disabled}
      maxWidth="md"
      fullWidth
      aria-labelledby="account-binding-review-title"
    >
      <DialogTitle id="account-binding-review-title">
        {kind === 'unclaim' ? 'Review new-person unclaim' : 'Review account binding'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          {applied && <Alert severity="success">Account access is now bound to the reviewed profile.</Alert>}

          {!summary && !applied && (
            <>
              <Typography>
                Load the server’s current plan to review the Auth account and profile records.
              </Typography>
              <Button variant="outlined" onClick={loadCurrentPlan} disabled={disabled}>
                {busy === 'loading' ? <CircularProgress size={20} aria-label="Loading plan" /> : 'Load current plan'}
              </Button>
            </>
          )}

          {summary && summary.kind === 'binding' && (
            <>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Auth account</Typography>
                <Typography><strong>Auth UID:</strong> {summary.authUid}</Typography>
                <Typography><strong>Target person ID:</strong> {summary.targetPersonId}</Typography>
              </Paper>

              {summary.former ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <ProfileCard title="Former profile" profile={summary.former} />
                  <ProfileCard title="Target profile" profile={summary.target} />
                </Box>
              ) : <ProfileCard title="Target profile" profile={summary.target} />}

              {summary.direct && (
                <>
                  <ProfileCard title="Detached account profile" profile={summary.direct} />
                  <Alert severity="info">
                    The account pointer will also be updated on this detached profile. Its person ID and history stay with this profile.
                  </Alert>
                </>
              )}

              <Alert severity="info">
                This changes which profile the Auth account can access. The runner’s person ID, race results,
                and registrations stay with their existing profile records.
              </Alert>

              {!applied && (
                <Stack spacing={0.5}>
                  <FormControlLabel
                    control={<Checkbox checked={reviewAcknowledged} onChange={event => setReviewAcknowledged(event.target.checked)} disabled={disabled} />}
                    label="I reviewed the Auth UID and all listed profile records, and confirm this account access change."
                  />
                </Stack>
              )}

              <Box display="flex" gap={1} flexWrap="wrap">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={approvePlan}
                  disabled={disabled || approved || applied || !reviewAcknowledged}
                >
                  {busy === 'approving' ? <CircularProgress size={20} aria-label="Approving plan" /> : 'Approve this plan'}
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={applyBinding}
                  disabled={disabled || !approved || applied}
                >
                  {busy === 'applying' ? <CircularProgress size={20} aria-label="Applying binding" /> : 'Apply account binding'}
                </Button>
              </Box>
            </>
          )}

          {summary && summary.kind === 'unclaim' && (
            <>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Auth account</Typography>
                <Typography><strong>Auth UID:</strong> {summary.authUid}</Typography>
              </Paper>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <ProfileCard title="Former profile" profile={summary.former} />
                <ProfileCard title="New person to be created" profile={summary.newPerson} />
              </Box>
              {summary.direct && (
                <>
                  <ProfileCard title="Detached account profile" profile={summary.direct} />
                  <Alert severity="info">
                    The account pointer will also be updated on this detached profile. Its person ID and history stay with this profile.
                  </Alert>
                </>
              )}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Personal detail retention</Typography>
                <Typography>
                  {summary.keepPersonalDetails ? 'Copy reviewed personal details' : 'Do not copy personal details'}
                </Typography>
                <Typography variant="body2">
                  Email, admin, and legacy fields are not copied. The former person ID, results, and registrations remain with the former profile; the Auth account receives a separate new person.
                </Typography>
              </Paper>
              <Alert severity="info">
                This changes which profile the Auth account can access. Existing person IDs, race results, and registrations stay with their profile records.
              </Alert>
              {!applied && (
                <FormControlLabel
                  control={<Checkbox checked={reviewAcknowledged} onChange={event => setReviewAcknowledged(event.target.checked)} disabled={disabled} />}
                  label="I reviewed the Auth UID and all listed profile records, and confirm this account access change."
                />
              )}
              <Box display="flex" gap={1} flexWrap="wrap">
                <Button variant="contained" color="primary" onClick={approvePlan}
                  disabled={disabled || approved || applied || !reviewAcknowledged}>
                  {busy === 'approving' ? <CircularProgress size={20} aria-label="Approving plan" /> : 'Approve this plan'}
                </Button>
                <Button variant="contained" color="success" onClick={applyBinding}
                  disabled={disabled || !approved || applied}>
                  {busy === 'applying' ? <CircularProgress size={20} aria-label="Applying binding" /> : 'Apply reviewed unclaim'}
                </Button>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={disabled}>{applied ? 'Done' : 'Close'}</Button>
      </DialogActions>
    </Dialog>
  );
};

const ProfileCard: React.FC<{ title: string; profile: ProfileSummary }> = ({ title, profile }) => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Typography variant="subtitle1" gutterBottom>{title}</Typography>
    {profile.displayName && <Typography><strong>Name:</strong> {profile.displayName}</Typography>}
    <Typography><strong>Users document ID:</strong> {profile.userId}</Typography>
    <Typography><strong>Person ID:</strong> {profile.personId ?? 'Unassigned'}</Typography>
    {profile.authUid !== undefined && (
      <Typography><strong>Linked Auth UID:</strong> {profile.authUid || 'None'}</Typography>
    )}
  </Paper>
);

export default AccountBindingReviewDialog;
